import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function BooksPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [selectedBook, setSelectedBook] = useState('');
  const [quantity, setQuantity] = useState(1);

  // Book prices
  const bookPrices: { [key: string]: number } = {
    'Pillow Talk - From Surface Talk to Soul Talk': 19.50,
    'He Said She Said - A Couple\'s Conversation': 19.50,
    'Destination Locked and Loaded - Are You Ready?': 19.50,
    'I Never Got To Say Goodbye!': 12.50,
    'The Last Game': 15.00
  };

  const shippingFee = 6.00;
  const bookPrice = selectedBook ? bookPrices[selectedBook] || 0 : 0;
  const subtotal = quantity * bookPrice;
  const total = selectedBook ? subtotal + shippingFee : 0;

  const books = [
    {
      id: 1,
      title: 'Pillow Talk',
      subtitle: 'From Surface Talk to Soul Talk',
      category: 'inspiration',
      author: 'Latonya Duncan',
      description: 'A heartfelt guide to deepening your conversations and connections. Learn how to move beyond surface-level interactions and engage in meaningful soul-to-soul communication with those you love.',
      image: 'https://static.readdy.ai/image/56b439000ec6e974a9f2c259ba0dbcf0/1565809714b517bdb6fa71a146d79c4b.png',
      price: '$19.50',
      link: '#order-form'
    },
    {
      id: 2,
      title: 'He Said She Said',
      subtitle: 'A Couple\'s Conversation',
      category: 'inspiration',
      author: 'LaTonya Duncan',
      description: 'An insightful exploration of relationship dynamics featuring contributions from 20 couples. Discover different perspectives on love, communication, and partnership through authentic conversations between partners.',
      image: 'https://static.readdy.ai/image/56b439000ec6e974a9f2c259ba0dbcf0/de36fe50b527645416374da1bb9b9a12.jpeg',
      price: '$19.50',
      link: '#order-form'
    },
    {
      id: 3,
      title: 'Destination Locked and Loaded',
      subtitle: 'Are You Ready?',
      category: 'inspiration',
      author: 'LaTonya Duncan',
      description: 'An empowering guide to help you prepare for your life\'s journey. This motivational book challenges you to get focused, set your goals, and move forward with purpose and determination toward your destination.',
      image: 'https://static.readdy.ai/image/56b439000ec6e974a9f2c259ba0dbcf0/abcf8f9a5cb571468d6655d760b4edde.jpeg',
      price: '$19.50',
      link: '#order-form'
    },
    {
      id: 4,
      title: 'I Never Got To Say Goodbye!',
      subtitle: '',
      category: 'inspiration',
      author: 'LaTonya Duncan',
      description: 'A touching children\'s book that gently explores themes of loss, love, and remembrance. This heartfelt story helps young readers understand and process difficult emotions while celebrating the lasting bonds we share with those we love.',
      image: 'https://static.readdy.ai/image/56b439000ec6e974a9f2c259ba0dbcf0/76068b43c00617a9ddf7c2a3612ebe13.jpeg',
      price: '$12.50',
      link: '#order-form'
    },
    {
      id: 5,
      title: 'The Last Game',
      subtitle: 'When one game ends, a new dream begins.',
      category: 'inspiration',
      author: 'LaTonya Duncan',
      description: 'An inspiring children\'s book about perseverance, dreams, and new beginnings. Follow a young baseball player as he learns that endings can lead to exciting new chapters in life, teaching young readers about resilience and hope.',
      image: 'https://static.readdy.ai/image/56b439000ec6e974a9f2c259ba0dbcf0/83df767fe28be4d0b9fc0883dc9f2b6a.png',
      price: '$15.00',
      link: '#order-form'
    }
  ];

  const categories = [
    { value: 'all', label: 'All Books' },
    { value: 'health', label: 'Health & Wellness' },
    { value: 'inspiration', label: 'Inspiration' },
    { value: 'wellness', label: 'Holistic Wellness' }
  ];

  const filteredBooks = activeCategory === 'all' 
    ? books 
    : books.filter(book => book.category === activeCategory);

  const handleOrderSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d61vf2rn6d2i4h4oaetg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you! Your book order has been received. We\'ll contact you shortly with payment and shipping details.');
        form.reset();
        setSelectedBook('');
        setQuantity(1);
      } else {
        setSubmitMessage('Something went wrong. Please try again or contact us directly.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-50 via-pink-50 to-coral-50 pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-charcoal mb-6">
              Books & Design
            </h1>
            <p className="text-lg text-charcoal/70 leading-relaxed">
              Explore our collection of inspiring books on health, wellness, storytelling, and personal growth. Each publication is crafted with care to empower and educate our community.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => setActiveCategory(category.value)}
                className={`px-6 py-2 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap text-sm ${
                  activeCategory === category.value
                    ? 'bg-coral text-white shadow-md'
                    : 'bg-gray-50 text-charcoal hover:bg-gray-100'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Books Grid */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBooks.map((book, index) => (
              <motion.div
                key={book.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all"
              >
                <div className="relative h-96 bg-gray-100">
                  <img
                    src={book.image}
                    alt={book.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <div className="text-xs font-semibold text-coral uppercase tracking-wide mb-2">
                    {book.author}
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-charcoal mb-1">
                    {book.title}
                  </h3>
                  {book.subtitle && (
                    <p className="text-charcoal/60 text-sm italic mb-3">{book.subtitle}</p>
                  )}
                  <p className="text-charcoal/70 text-sm leading-relaxed mb-4">
                    {book.description}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-2xl font-bold text-charcoal">{book.price}</span>
                    <a
                      href={book.link}
                      onClick={() => setSelectedBook(book.title + (book.subtitle ? ` - ${book.subtitle}` : ''))}
                      className="bg-coral text-white px-6 py-2 rounded-full font-semibold hover:bg-coral/90 transition-all cursor-pointer whitespace-nowrap text-sm"
                    >
                      Purchase
                      <i className="ri-shopping-cart-line ml-2"></i>
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Book Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block bg-coral/10 text-coral px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Reader Reviews
            </span>
            <h2 className="font-serif text-4xl font-bold text-charcoal mb-4">
              What Our Readers Say
            </h2>
            <p className="text-charcoal/70 max-w-2xl mx-auto">
              Discover how these books have touched hearts and transformed lives in our community.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                id: 1,
                book: 'Pillow Talk',
                quote: 'This book transformed my marriage. My husband and I now have deeper conversations than ever before. It taught us how to truly listen and connect on a soul level.',
                name: 'Angela R.',
                location: 'Atlanta, GA',
                rating: 5,
                image: 'https://public.readdy.ai/ai/img_res/497857a7de6831ec9fe3e40b7a5e18d2.jpg'
              },
              {
                id: 2,
                book: 'He Said She Said',
                quote: 'Reading perspectives from 20 different couples opened my eyes to so many relationship dynamics. It helped me understand my partner better and appreciate our differences.',
                name: 'Marcus & Tanya J.',
                location: 'Houston, TX',
                rating: 5,
                image: 'https://readdy.ai/api/search-image?query=portrait%20of%20happy%20african%20american%20couple%20in%20their%2030s%20smiling%20together%20wearing%20casual%20clothing%20against%20soft%20neutral%20background%20representing%20loving%20relationship%20and%20partnership%20with%20warm%20natural%20lighting&width=200&height=200&seq=review002&orientation=squarish'
              },
              {
                id: 3,
                book: 'Destination Locked and Loaded',
                quote: 'I was stuck in life with no direction. This book gave me the motivation and practical steps to set goals and actually achieve them. I finally feel like I am in control of my future.',
                name: 'David M.',
                location: 'Charlotte, NC',
                rating: 5,
                image: 'https://readdy.ai/api/search-image?query=portrait%20of%20confident%20young%20black%20man%20in%20his%2030s%20with%20determined%20expression%20wearing%20professional%20casual%20attire%20against%20soft%20neutral%20background%20representing%20personal%20growth%20and%20success%20with%20natural%20lighting&width=200&height=200&seq=review003&orientation=squarish'
              },
              {
                id: 4,
                book: 'I Never Got To Say Goodbye!',
                quote: 'This book helped my daughter process the loss of her grandmother. The gentle storytelling made it easier for us to talk about grief together. A must-have for any family.',
                name: 'Patricia W.',
                location: 'Birmingham, AL',
                rating: 5,
                image: 'https://readdy.ai/api/search-image?query=portrait%20of%20caring%20mother%20in%20her%2030s%20with%20warm%20smile%20wearing%20casual%20clothing%20against%20soft%20cream%20background%20representing%20loving%20parent%20and%20book%20reader%20with%20natural%20lighting%20and%20genuine%20compassionate%20expression&width=200&height=200&seq=review004&orientation=squarish'
              },
              {
                id: 5,
                book: 'The Last Game',
                quote: 'My son was devastated when his baseball season ended. This book taught him that endings lead to new beginnings. He now looks forward to trying new things. Beautiful story!',
                name: 'Robert T.',
                location: 'Memphis, TN',
                rating: 5,
                image: 'https://readdy.ai/api/search-image?query=portrait%20of%20proud%20father%20in%20his%2040s%20with%20warm%20smile%20wearing%20casual%20polo%20shirt%20against%20soft%20neutral%20background%20representing%20supportive%20parent%20with%20natural%20lighting%20and%20genuine%20happy%20expression&width=200&height=200&seq=review005&orientation=squarish'
              },
              {
                id: 6,
                book: 'Pillow Talk',
                quote: 'As a marriage counselor, I recommend this book to all my clients. It provides practical tools for meaningful communication that actually work. Beautifully written!',
                name: 'Dr. Michelle S.',
                location: 'Nashville, TN',
                rating: 5,
                image: 'https://readdy.ai/api/search-image?query=portrait%20of%20professional%20african%20american%20woman%20therapist%20in%20her%2050s%20with%20confident%20warm%20smile%20wearing%20elegant%20blouse%20against%20soft%20neutral%20background%20representing%20expertise%20and%20wisdom%20with%20natural%20lighting&width=200&height=200&seq=review006&orientation=squarish'
              }
            ].map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-cream to-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-all border border-gray-100"
              >
                {/* Book Badge */}
                <div className="inline-block bg-coral/10 text-coral px-3 py-1 rounded-full text-xs font-semibold mb-4">
                  {testimonial.book}
                </div>

                {/* Rating */}
                <div className="flex text-amber-400 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="text-charcoal/80 text-sm leading-relaxed mb-6 italic">
                  "{testimonial.quote}"
                </blockquote>

                {/* Reviewer Info */}
                <div className="flex items-center space-x-3 pt-4 border-t border-gray-100">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <div className="font-semibold text-charcoal text-sm">{testimonial.name}</div>
                    <div className="text-charcoal/60 text-xs">{testimonial.location}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Overall Rating Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-16 bg-gradient-to-r from-coral/10 via-pink-50 to-coral/10 rounded-2xl p-8 text-center"
          >
            <div className="flex justify-center items-center space-x-2 mb-3">
              <span className="text-4xl font-bold text-charcoal">4.9</span>
              <div className="flex text-amber-400">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-xl"></i>
                ))}
              </div>
            </div>
            <p className="text-charcoal/70 text-sm">
              Based on <span className="font-semibold text-charcoal">150+ verified reviews</span> from our readers
            </p>
          </motion.div>
        </div>
      </section>

      {/* Order Form Section */}
      <section id="order-form" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 bg-coral rounded-full mx-auto mb-6 flex items-center justify-center">
              <i className="ri-shopping-bag-3-line text-3xl text-white"></i>
            </div>
            <h2 className="font-serif text-4xl font-bold text-charcoal mb-4">
              Order Your Books
            </h2>
            <p className="text-charcoal/70 text-lg">
              Fill out the form below to place your order. We'll contact you with payment and shipping details.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            onSubmit={handleOrderSubmit}
            data-readdy-form
            className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl shadow-xl p-8 md:p-12"
          >
            <div className="space-y-6">
              {/* Book Selection */}
              <div>
                <label htmlFor="book" className="block text-sm font-semibold text-charcoal mb-2">
                  Select Book <span className="text-coral">*</span>
                </label>
                <select
                  id="book"
                  name="book"
                  required
                  value={selectedBook}
                  onChange={(e) => setSelectedBook(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none transition-all text-sm"
                >
                  <option value="">Choose a book...</option>
                  <option value="Pillow Talk - From Surface Talk to Soul Talk">Pillow Talk - From Surface Talk to Soul Talk ($19.50)</option>
                  <option value="He Said She Said - A Couple's Conversation">He Said She Said - A Couple's Conversation ($19.50)</option>
                  <option value="Destination Locked and Loaded - Are You Ready?">Destination Locked and Loaded - Are You Ready? ($19.50)</option>
                  <option value="I Never Got To Say Goodbye!">I Never Got To Say Goodbye! ($12.50)</option>
                  <option value="The Last Game">The Last Game ($15.00)</option>
                </select>
              </div>

              {/* Quantity */}
              <div>
                <label htmlFor="quantity" className="block text-sm font-semibold text-charcoal mb-2">
                  Quantity <span className="text-coral">*</span>
                </label>
                <input
                  type="number"
                  id="quantity"
                  name="quantity"
                  min="1"
                  max="50"
                  required
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value))}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none transition-all text-sm"
                />
              </div>

              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-charcoal mb-2">
                  Full Name <span className="text-coral">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none transition-all text-sm"
                  placeholder="John Doe"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-charcoal mb-2">
                  Email Address <span className="text-coral">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none transition-all text-sm"
                  placeholder="john@example.com"
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-charcoal mb-2">
                  Phone Number <span className="text-coral">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none transition-all text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>

              {/* Shipping Address */}
              <div>
                <label htmlFor="address" className="block text-sm font-semibold text-charcoal mb-2">
                  Shipping Address <span className="text-coral">*</span>
                </label>
                <textarea
                  id="address"
                  name="address"
                  required
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none transition-all text-sm resize-none"
                  placeholder="Street address, city, state, ZIP code"
                ></textarea>
                <p className="text-xs text-charcoal/50 mt-1">Maximum 500 characters</p>
              </div>

              {/* Additional Notes */}
              <div>
                <label htmlFor="notes" className="block text-sm font-semibold text-charcoal mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none transition-all text-sm resize-none"
                  placeholder="Any special instructions or questions..."
                ></textarea>
                <p className="text-xs text-charcoal/50 mt-1">Maximum 500 characters</p>
              </div>

              {/* Order Summary */}
              {selectedBook && (
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <h3 className="font-semibold text-charcoal mb-4 flex items-center">
                    <i className="ri-file-list-3-line mr-2 text-coral"></i>
                    Order Summary
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between text-charcoal/70">
                      <span>Book Price ({quantity} × ${bookPrice.toFixed(2)})</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-charcoal/70">
                      <span>Shipping Fee</span>
                      <span>${shippingFee.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-gray-200 pt-3 flex justify-between font-bold text-charcoal">
                      <span>Total</span>
                      <span className="text-coral">${total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Hidden field for total */}
              <input type="hidden" name="order_total" value={total.toFixed(2)} />
              <input type="hidden" name="shipping_fee" value={shippingFee.toFixed(2)} />

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-coral text-white px-8 py-4 rounded-full font-semibold hover:bg-coral/90 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Processing...
                  </>
                ) : (
                  <>
                    Place Order
                    <i className="ri-send-plane-fill ml-2"></i>
                  </>
                )}
              </button>

              {/* Submit Message */}
              {submitMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-lg text-sm ${
                    submitMessage.includes('Thank you')
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                >
                  {submitMessage}
                </motion.div>
              )}
            </div>
          </motion.form>
        </div>
      </section>

      {/* Design Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl font-bold text-charcoal mb-4">
              Creative Design Services
            </h2>
            <p className="text-charcoal/70 max-w-2xl mx-auto">
              Beyond books, we offer creative design services for health education materials, event branding, and community outreach campaigns.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-book-open-line',
                title: 'Book Design & Publishing',
                description: 'Professional book cover design, layout, and self-publishing guidance for health and wellness authors.'
              },
              {
                icon: 'ri-palette-line',
                title: 'Brand Identity',
                description: 'Custom branding solutions for health organizations, nonprofits, and wellness initiatives.'
              },
              {
                icon: 'ri-layout-line',
                title: 'Educational Materials',
                description: 'Engaging infographics, brochures, and digital content for health education programs.'
              }
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-100"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-purple-600 rounded-full mb-6">
                  <i className={`${service.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="font-serif text-xl font-bold text-charcoal mb-3">
                  {service.title}
                </h3>
                <p className="text-charcoal/70 text-sm leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/contact"
              className="inline-flex items-center bg-purple-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-purple-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              Inquire About Design Services
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Author Bio Section */}
      <section className="py-20 bg-gradient-to-br from-coral-50 to-sage-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl shadow-xl p-8 md:p-12"
          >
            <div className="text-center mb-8">
              <div className="w-24 h-24 bg-gradient-to-br from-coral to-purple-600 rounded-full mx-auto mb-6 flex items-center justify-center">
                <i className="ri-quill-pen-line text-4xl text-white"></i>
              </div>
              <h2 className="font-serif text-3xl font-bold text-charcoal mb-4">
                About the Author
              </h2>
              <p className="text-charcoal/70 leading-relaxed">
                Our books are written by passionate health educators, wellness coaches, and community leaders dedicated to empowering individuals through knowledge and inspiration. Each publication reflects years of experience in health education, community outreach, and personal transformation.
              </p>
            </div>
            <div className="flex justify-center gap-4">
              <Link
                to="/about"
                className="inline-flex items-center text-coral font-semibold hover:gap-2 transition-all cursor-pointer whitespace-nowrap"
              >
                Learn More About Us
                <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-charcoal text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-4xl font-bold mb-6">
            Stay Updated on New Releases
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            Subscribe to our newsletter to be the first to know about new book releases, special offers, and exclusive content.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-coral text-white px-8 py-4 rounded-full font-semibold hover:bg-coral/90 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
          >
            Subscribe Now
            <i className="ri-mail-line ml-2"></i>
          </Link>
        </div>
      </section>
    </div>
  );
}
